import type { NotificationBellProps } from '@/types/notification'
import { Bell } from 'lucide-react'
import * as React from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const sizeConfig = {
  sm: {
    button: 'h-8 w-8',
    icon: 'h-4 w-4',
    badge: 'h-4 min-w-[1rem] text-[10px] px-1 inline-flex items-center justify-center',
  },
  default: {
    button: 'h-10 w-10',
    icon: 'h-5 w-5',
    badge: 'h-5 min-w-[1.25rem] text-xs px-1 inline-flex items-center justify-center',
  },
  lg: {
    button: 'h-12 w-12',
    icon: 'h-6 w-6',
    badge: 'h-6 min-w-[1.5rem] text-sm px-1.5 inline-flex items-center justify-center',
  },
}

export const NotificationBell = React.forwardRef<HTMLButtonElement, NotificationBellProps>(
  ({ unreadCount = 0, onClick, className, size = 'default', ...props }, ref) => {
    const config = sizeConfig[size]

    return (
      <Button
        ref={ref}
        variant="ghost"
        size="icon"
        className={cn('relative', config.button, className)}
        onClick={onClick}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        {...props}
      >
        <Bell className={config.icon} />
        {unreadCount > 0 && (
          <Badge
            variant="destructive"
            className={cn('absolute -top-1 -right-1 rounded-full', config.badge)}
          >
            <span className="sr-only">
              {unreadCount}
              {' '}
              unread notifications
            </span>
            <span aria-hidden="true">{unreadCount > 99 ? '99+' : unreadCount}</span>
          </Badge>
        )}
      </Button>
    )
  },
)

NotificationBell.displayName = 'NotificationBell'
